import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ConditionalRender } from '@ui';
import { PermissionRequiredModal } from '@features/components/CaseFormBlocks/components';
import { CustomerPermissionOnly, CustomerRoleOnly } from '@/api/apis/customer';
import { CreateTXUserFormValues } from '@/types/customer';
import { RoleSelector } from './RoleSelector';
import { PermissionSelector } from './PermissionSelector';
import { useRoleFormLogic } from './useRoleFormLogic';

type TXUserRoleCreateFormProps = {
  isReadOnly?: boolean;
};

export const TXUserRoleCreateForm = ({ isReadOnly = false }: TXUserRoleCreateFormProps) => {
  const { setValue, trigger, control } = useFormContext<CreateTXUserFormValues>();
  const rolesRaw = useWatch({ control, name: 'roles' }) as CustomerRoleOnly[] | undefined;
  const permissionsRaw = useWatch({ control, name: 'permissions' }) as CustomerPermissionOnly[] | undefined;
  const roleValues = useMemo<CustomerRoleOnly[]>(() => rolesRaw ?? [], [rolesRaw]);
  const permissionValues = useMemo<CustomerPermissionOnly[]>(() => permissionsRaw ?? [], [permissionsRaw]);

  const updateRoles = useCallback(
    (roles: CustomerRoleOnly[]) => {
      setValue('roles', roles, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  const updatePermissions = useCallback(
    (perms: CustomerPermissionOnly[]) => {
      setValue('permissions', perms, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  const triggerBoth = useCallback(async () => {
    const results = await Promise.all([trigger('roles'), trigger('permissions')]);
    return results as [boolean, boolean];
  }, [trigger]);

  const { handleRoleChange, handlePermissionChange, permissionRequiredModalData, setPermissionRequiredModalData } =
    useRoleFormLogic({
      roleValues,
      permissionValues,
      updateRoles,
      updatePermissions,
      triggerBoth,
    });

  return (
    <div className="flex flex-col gap-6">
      <RoleSelector name="roles" roleValues={roleValues} handleRoleChange={handleRoleChange} isReadOnly={isReadOnly} />
      <PermissionSelector
        name="permissions"
        roleValues={roleValues}
        permissionValues={permissionValues}
        handlePermissionChange={handlePermissionChange}
        isReadOnly={isReadOnly}
      />
      <ConditionalRender show={Boolean(permissionRequiredModalData)}>
        <PermissionRequiredModal
          data={permissionRequiredModalData!}
          onClose={() => setPermissionRequiredModalData(null)}
        />
      </ConditionalRender>
    </div>
  );
};
